import React, { FC } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { useSelector } from 'react-redux';
import { trackEvent } from 'dibs-tracking';
import { createFragmentContainer, graphql } from 'react-relay';
import authModalLoader from '../../utils/AuthModalLoader';
import {
    CREATE_PASSWORD_MY_ORDERS_FLOW,
    CREATE_PASSWORD_MY_CONVERSATIONS_FLOW,
    LOGIN,
    REQUEST_PASSWORD_EMAIL,
} from '../../authModal/authentication/authFlowConstants';
import { Link } from 'dibs-elements/exports/Link';

import styles from './AccountNav-style.scss';

import { AccountNavContent_user as UserType } from './__generated__/AccountNavContent_user.graphql';
import { AccountNavContent_viewer as ViewerType } from './__generated__/AccountNavContent_viewer.graphql';

const trackingObj = {
    category: 'navigation',
    action: 'header navigation click',
};

type ReduxStoreType = {
    header: {
        logoutUser: () => void;
        isEmailOnly: boolean;
    };
};

const messages = defineMessages({
    accountLabel: { id: 'dbl.Header.AccountNavContent.accountLabel', defaultMessage: 'Account' },
    logout: { id: 'dbl.Header.AccountNavContent.logout', defaultMessage: 'Logout' },
    myOrders: { id: 'dbl.Header.AccountNavContent.myOrders', defaultMessage: 'Orders' },
    accountInformation: {
        id: 'dbl.Header.AccountNavContent.accountInformation',
        defaultMessage: 'Account Information',
    },
    myConversations: {
        id: 'dbl.Header.AccountNavContent.myConversations',
        defaultMessage: 'Inbox',
    },
});

type AccountNavDropdownContentProps = {
    user: UserType;
    viewer: ViewerType;
};

const AccountNavDropdownContent: FC<AccountNavDropdownContentProps> = ({ viewer, user }) => {
    const { isMessageCenterEnabled } = viewer;
    const totalUnreadConversations = user?.conversationSummary?.unread || 0;
    const logoutUser = useSelector<ReduxStoreType, () => void>(({ header }) => header.logoutUser);
    const hasEmailToken = useSelector<ReduxStoreType, boolean>(({ header }) => header.isEmailOnly);
    const intl = useIntl();
    const onLogoutClick = (): void => {
        trackEvent({ ...trackingObj, label: 'user-nav|logout' });
        logoutUser();
    };

    const showAuthModal: (args: { flow: string; url: string }) => void = async ({ flow, url }) => {
        const email = user?.profile?.email;
        const res = await authModalLoader.show({
            flow,
            action: REQUEST_PASSWORD_EMAIL,
            email,
        });

        if (!res.err && res.modal === LOGIN) {
            window.location.href = url;
        }
    };

    return (
        <div className={styles.dropdownContent}>
            <div
                className={styles.dropdownLinkWrapper}
                data-tn="sub-nav-list-item-account-information"
            >
                <Link
                    className={styles.dropdownLink}
                    href="/my/account/"
                    aria-label={intl.formatMessage(messages.accountLabel)}
                    dataTn="sub-nav-list-item-link-account-information"
                    onClick={() => {
                        trackEvent({ ...trackingObj, label: 'user-nav|account information' });
                    }}
                >
                    {intl.formatMessage(messages.accountInformation)}
                </Link>
            </div>
            <div className={styles.dropdownLinkWrapper} data-tn="sub-nav-list-item-my-orders">
                <Link
                    className={styles.dropdownLink}
                    dataTn="sub-nav-list-item-link-my-orders"
                    onClick={async () => {
                        const url = '/orders/';
                        if (hasEmailToken) {
                            await showAuthModal({ flow: CREATE_PASSWORD_MY_ORDERS_FLOW, url });
                        } else {
                            trackEvent({ ...trackingObj, label: 'user-nav|my orders' });
                            window.location.href = url;
                        }
                    }}
                >
                    {intl.formatMessage(messages.myOrders)}
                </Link>
            </div>

            {isMessageCenterEnabled && (
                <div
                    className={styles.dropdownLinkWrapper}
                    data-tn="sub-nav-list-item-my-conversations"
                >
                    <Link
                        className={styles.dropdownLink}
                        dataTn="sub-nav-list-item-link-my-conversations"
                        onClick={async () => {
                            const url = '/inbox/';
                            if (hasEmailToken) {
                                await showAuthModal({
                                    flow: CREATE_PASSWORD_MY_CONVERSATIONS_FLOW,
                                    url,
                                });
                            } else {
                                trackEvent({
                                    ...trackingObj,
                                    label: 'user-nav|my conversations',
                                });
                                window.location.href = url;
                            }
                        }}
                    >
                        {intl.formatMessage(messages.myConversations)}
                        {totalUnreadConversations ? (
                            <span className={styles.unreadCount}>
                                {' '}
                                ({totalUnreadConversations})
                            </span>
                        ) : null}
                    </Link>
                </div>
            )}

            <div className={styles.dropdownLinkWrapper} data-tn="sub-nav-list-item-logout">
                <span
                    className={styles.dropdownLink}
                    data-tn="sub-nav-list-item-link-logout"
                    role="button"
                    tabIndex={-1}
                    onClick={onLogoutClick}
                    onKeyPress={onLogoutClick}
                >
                    {intl.formatMessage(messages.logout)}
                </span>
            </div>
        </div>
    );
};

export default createFragmentContainer(AccountNavDropdownContent, {
    viewer: graphql`
        fragment AccountNavContent_viewer on Viewer {
            isMessageCenterEnabled: isEnabledByLocale(featureName: "MESSAGE_CENTER")
        }
    `,
    user: graphql`
        fragment AccountNavContent_user on User {
            profile {
                email
            }
            conversationSummary {
                unread
            }
        }
    `,
});
